import { useEffect, type ReactNode } from 'react'
import { Lock } from 'lucide-react'
import { Mark } from '../Logo.js'
import { AuthAnimatedBackground } from './AuthAnimatedBackground.js'
import { usePrefsStore, syncFromServer } from '../prefs.js'

/**
 * Gabarit commun des écrans d'authentification.
 * Hiérarchie SIMPLE, imposée : identité → titre → contenu → état de sécurité.
 * Le fond est celui du design system (navy + nappes), rien de plus.
 */
export function AuthShell(props: {
  title: string
  subtitle?: string
  children: ReactNode
  /** Fond animé atténué — pour les écrans denses (codes de secours). */
  fondDiscret?: boolean | undefined
}) {
  // LOT 5.5 : les écrans d'auth consomment le BRANDING PUBLIC (logo, nom,
  // organisation) et les défauts visuels de l'organisation — jamais les
  // préférences privées d'un compte (il n'y a pas encore de session).
  const { branding } = usePrefsStore()
  useEffect(() => {
    const r = document.documentElement
    if (!r.getAttribute('data-intensity')) r.setAttribute('data-intensity', 'premium')
    if (!r.getAttribute('data-theme')) r.setAttribute('data-theme', 'dark')
    void syncFromServer()
  }, [])

  return (
    <>
      <div className="lc-bg" aria-hidden="true">
        <div className="lc-bg__wash-a" />
        <div className="lc-bg__wash-b" />
        <div className="lc-bg__spot" />
        <AuthAnimatedBackground dim={props.fondDiscret} />
      </div>

      <main className="lc-auth">
        <section className="lc-auth__card" aria-labelledby="auth-title">
          <span className="lc-auth__edge" aria-hidden="true" />
          <header className="lc-auth__brand">
            <span className="lc-auth__mark" aria-hidden="true">
              {branding.assets['logo_compact']
                ? <img src={branding.assets['logo_compact']!.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                : <Mark size={30} />}
            </span>
            <div>
              <h1 className="lc-auth__title" id="auth-title">{props.title}</h1>
              {props.subtitle ? <p className="lc-auth__sub">{props.subtitle}</p> : null}
            </div>
          </header>

          {props.children}

          <footer className="lc-auth__foot">
            <Lock size={13} aria-hidden="true" />
            <span>Portail interne — {branding.org_name}</span>
          </footer>
        </section>
      </main>
    </>
  )
}
